import React, { useState } from 'react';
import { Menu, MenuItem, Typography, Box, Badge, Divider, ListItemIcon, ListItemText } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { capitalize } from 'helpers/format';
import { loadCustomerAction, loadRolesAction } from 'store/actions/appActions';
import Customer from 'types/entities/Customer';

const CustomerSelect: React.FC = () => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const customers: Customer[] = appState.customers || [];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(appState.customer || null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (customer?: Customer) => {
    setAnchorEl(null);

    if (!customer) return; // just close menu if no customer selected

    const previous_customer: string | null = localStorage.getItem('active_customer');
    if (previous_customer === JSON.stringify(customer)) {
      return;
    }
    // Save active customer to localStorage
    localStorage.setItem('active_customer', JSON.stringify(customer));

    // Dispatch actions
    dispatch(loadCustomerAction(customer));
    dispatch(loadRolesAction());
    localStorage.setItem('navigation_screen', JSON.stringify('customer')); // Clear active role on customer change
  };

  const buttonLabel = selectedCustomer ? capitalize(selectedCustomer.name) : appState.customer?.name || '';

  return (
    <>
      {/* Topbar display */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'text.primary',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {buttonLabel}
        </Typography>
        <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
      </Box>

      {/* Dropdown Menu */}
      <Menu
        id="customer-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 240,
            borderRadius: 2,
            mt: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Client
          </Typography>
        </Box>
        {/* <Divider /> */}

        {/* Customer list */}
        {customers.map((item) => {
          const isSelected = selectedCustomer?.id === item.id;
          return (
            <MenuItem
              key={item.id}
              onClick={() => handleClose(item)}
              selected={isSelected}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isSelected && (
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                )}
                <ListItemText
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {capitalize(item.name)}
                </ListItemText>
              </Box>

              {/* Example badge (replace item.count with your real data field) */}
              {item?.count && (
                <Badge
                  badgeContent={item?.count}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -6,
                      top: 2,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      height: 18,
                      minWidth: 18,
                    },
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CustomerSelect;
